import * as Sentry from '@sentry/react'
import { apiTraceTargets, scrubSentryEvent } from './observability'

export function initSentry() {
  if (!import.meta.env.VITE_SENTRY_DSN) {
    console.warn('Sentry DSN not configured, skipping initialization')
    return
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    release: __SENTRY_RELEASE__ || undefined,
    initialScope: { tags: { app: 'admin' } },

    tracesSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE || '1.0'
    ),

    replaysSessionSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1'
    ),
    replaysOnErrorSampleRate: parseFloat(
      import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0'
    ),

    tracePropagationTargets: apiTraceTargets(import.meta.env.VITE_API_URL),

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: true,
        networkDetailAllowUrls: apiTraceTargets(import.meta.env.VITE_API_URL),
        networkCaptureBodies: false,
        networkRequestHeaders: ['X-Request-ID'],
        networkResponseHeaders: ['X-Request-ID', 'X-Trace-ID'],
      }),
    ],

    ignoreErrors: [
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      'CodeUnauthenticated',
      // Network-level failures from the browser fetch — user offline, tunnel,
      // CORS preflight drop, OS sleep mid-request. Not actionable on our side.
      'Failed to fetch',
      'NetworkError when attempting to fetch resource',
      'Load failed',
    ],

    beforeSend(event, _hint) {
      if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_DEBUG) {
        console.log('[Sentry Debug]', event)
        return null
      }

      return scrubSentryEvent(event)
    },
    beforeSendTransaction: scrubSentryEvent,
    beforeBreadcrumb: scrubSentryEvent,
  })
}

export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  })
}

export { Sentry }
